/**
 * Utilities for secure cross-domain communication with Google Picker popup
 * Uses postMessage API for communication between tenant subdomain and auth domain
 */

export interface GooglePickerResult {
    success: boolean;
    url?: string;
    accessToken?: string;
    error?: string;
}

export interface GooglePickerMessage {
    type: "GOOGLE_PICKER_RESULT" | "GOOGLE_PICKER_READY" | "GOOGLE_PICKER_CANCEL";
    payload?: GooglePickerResult;
    origin?: string;
}

/**
 * Validates that a message origin is from an allowed domain
 * @param origin - The origin of the incoming message
 * @param allowedDomains - List of allowed domain patterns (e.g., "kenbakke.com")
 * @returns boolean indicating if origin is valid
 */
export const isValidOrigin = (origin: string, allowedDomains: string[]): boolean => {
    try {
        const url = new URL(origin);
        const hostname = url.hostname;

        // Allow localhost for development
        if (hostname === "localhost" || hostname === "127.0.0.1") {
            return true;
        }

        // Check if hostname ends with any of the allowed domains
        return allowedDomains.some(domain => {
            return hostname === domain || hostname.endsWith(`.${domain}`);
        });
    } catch {
        return false;
    }
};

/**
 * Gets the main domain from the current hostname
 * Used to validate origins belong to the same organization
 */
export const getMainDomain = (): string => {
    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "localhost";
    }

    const parts = hostname.split(".");
    // Return last two parts (e.g., "kenbakke.com" from "tenant.kenbakke.com")
    return parts.slice(-2).join(".");
};

/**
 * Sends a message to the parent window (from popup to opener)
 * @param message - The message to send
 * @param targetOrigin - The target origin (use '*' only if necessary, prefer specific origin)
 */
export const sendMessageToOpener = (message: GooglePickerMessage, targetOrigin: string): void => {
    console.log("sendMessageToOpener: Attempting to send message", {
        type: message.type,
        targetOrigin
    });
    console.log("sendMessageToOpener: window.opener exists:", !!window.opener);
    console.log("sendMessageToOpener: window.opener.closed:", window.opener?.closed);

    if (window.opener && !window.opener.closed) {
        try {
            window.opener.postMessage(message, targetOrigin);
            console.log("sendMessageToOpener: Message sent successfully");
        } catch (error) {
            console.error("sendMessageToOpener: Failed to send message", error);
        }
    } else {
        console.warn("sendMessageToOpener: No opener window available or it is closed");
    }
};

/**
 * Sends the picker result to the parent window and closes the popup
 * @param result - The picker result
 * @param targetOrigin - The origin of the parent window
 */
export const sendPickerResultAndClose = (
    result: GooglePickerResult,
    targetOrigin: string
): void => {
    const message: GooglePickerMessage = {
        type: "GOOGLE_PICKER_RESULT",
        payload: result,
        origin: window.location.origin
    };

    sendMessageToOpener(message, targetOrigin);

    // Give time for message to be received before closing
    setTimeout(() => {
        window.close();
    }, 100);
};

/**
 * Notifies the parent window that the picker is ready
 * @param targetOrigin - The origin of the parent window
 */
export const notifyPickerReady = (targetOrigin: string): void => {
    const message: GooglePickerMessage = {
        type: "GOOGLE_PICKER_READY",
        origin: window.location.origin
    };

    sendMessageToOpener(message, targetOrigin);
};

/**
 * Notifies the parent window that the picker was cancelled
 * @param targetOrigin - The origin of the parent window
 */
export const notifyPickerCancel = (targetOrigin: string): void => {
    const message: GooglePickerMessage = {
        type: "GOOGLE_PICKER_CANCEL",
        origin: window.location.origin
    };

    sendMessageToOpener(message, targetOrigin);

    setTimeout(() => {
        window.close();
    }, 100);
};

/**
 * Opens a centered popup window
 * @param url - URL to open
 * @param name - Window name
 * @param width - Window width
 * @param height - Window height
 * @returns Window reference or null
 */
export const openCenteredPopup = (
    url: string,
    name: string,
    width = 800,
    height = 600
): Window | null => {
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    return window.open(
        url,
        name,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
};
